import React from "react";
import { IoClose } from "react-icons/io5";

const PERMISSION_KEYS = [
  { key: "isView", label: "View" },
  { key: "isEdit", label: "Edit" },
  { key: "isDelete", label: "Delete" },
];

/**
 * Manage Members modal — reused for both organization-level and folder-level
 * user assignments.
 *
 * Purely presentational: the parent owns the queries/mutations and passes
 * derived lists (visibleAssignedUsers) + handlers in.
 */
const ManageMembersModal = ({
  isOpen,
  memberOrg,
  memberFolder,
  visibleAssignedUsers,
  allUsers,
  selectedUserToAssign,
  setSelectedUserToAssign,
  memberPermissions,
  toggleMemberPermission,
  rolePermLoading,
  mutateAssignedUserMutation,
  onAddMember,
  onRemoveMember,
  onClose,
}) => {
  if (!isOpen || !memberOrg) return null;

  const targetName = memberFolder
    ? memberFolder.folderName ?? memberFolder.name
    : memberOrg.organizationsName;

  const isBusy = mutateAssignedUserMutation.isPending;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-sm font-bold text-slate-800">
            Manage Members — {targetName}
          </h3>
          <button
            onClick={onClose}
            disabled={isBusy}
            className="text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <IoClose size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Assigned List */}
          <div className="space-y-2">
            <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Current Members
            </h4>
            <div className="border border-slate-100 rounded-xl overflow-hidden divide-y divide-slate-100 max-h-[180px] overflow-y-auto scroll-inner">
              {visibleAssignedUsers.length === 0 ? (
                <div className="p-4 text-center text-xs text-slate-400 font-medium">
                  {memberFolder
                    ? "No members assigned to this folder."
                    : "No members assigned to this organization."}
                </div>
              ) : (
                visibleAssignedUsers.map((item) => {
                  const permBadges = [
                    item.isView && { label: "View", cls: "bg-emerald-100 text-emerald-700" },
                    item.isEdit && { label: "Edit", cls: "bg-blue-100 text-blue-700" },
                    item.isDelete && { label: "Delete", cls: "bg-red-100 text-red-700" },
                  ].filter(Boolean);

                  return (
                    <div
                      key={item.organizationsDNSAssignedId}
                      className="flex items-center justify-between gap-2 p-3 bg-white hover:bg-slate-50/50 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-slate-700">
                            {item.assignedUserName}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold bg-purple-100 text-purple-700 uppercase tracking-wider">
                            {item.assignedUserRole}
                          </span>
                        </div>
                        {permBadges.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            {permBadges.map((b) => (
                              <span
                                key={b.label}
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold ${b.cls}`}
                              >
                                {b.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() =>
                          onRemoveMember(item.organizationsDNSAssignedId, item.assignedUserId)
                        }
                        disabled={isBusy}
                        className="px-2.5 py-1 text-[11px] font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-800 rounded-lg transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Add Member Form */}
          <div className="space-y-3 pt-2 border-t border-slate-100">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Add Member (name)
              </label>
              <select
                value={selectedUserToAssign}
                disabled={isBusy}
                onChange={(e) => setSelectedUserToAssign(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="">Select a user...</option>
                {allUsers
                  .filter(
                    (u) =>
                      !visibleAssignedUsers.some((au) => au.assignedUserId === u.userID)
                  )
                  .map((u) => (
                    <option key={u.userID} value={u.userID}>
                      {u.userName} ({u.userRoleName || "User"})
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Permissions
              </label>
              {!selectedUserToAssign ? (
                <p className="text-[11px] text-slate-400 font-medium">
                  Select a user to load role permissions.
                </p>
              ) : rolePermLoading ? (
                <div className="flex items-center gap-2 py-1.5">
                  <div className="w-3.5 h-3.5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-[11px] text-slate-500 font-medium">
                    Loading role permissions...
                  </span>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {PERMISSION_KEYS.map((perm) => {
                    const active = !!memberPermissions[perm.key]?.checked;
                    const allowedByRole = !!memberPermissions[perm.key]?.allowed;
                    const isViewLocked = perm.key === "isView";
                    const isDisabled = isBusy || isViewLocked || !allowedByRole;

                    return (
                      <button
                        key={perm.key}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => toggleMemberPermission(perm.key)}
                        title={
                          isViewLocked
                            ? "View permission is always required"
                            : !allowedByRole
                              ? "Not available for this user role"
                              : active
                                ? "Click to uncheck"
                                : "Click to check"
                        }
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${isDisabled
                          ? active && allowedByRole
                            ? "bg-indigo-50 border-indigo-200 text-indigo-400 cursor-not-allowed opacity-80"
                            : "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed opacity-60"
                          : active
                            ? "bg-indigo-50 border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                            : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"
                          }`}
                      >
                        <span
                          className={`w-3.5 h-3.5 rounded border flex items-center justify-center text-[9px] ${active
                            ? isDisabled && !allowedByRole
                              ? "bg-slate-300 border-slate-300 text-white"
                              : "bg-indigo-600 border-indigo-600 text-white"
                            : "border-slate-300 bg-white"
                            }`}
                        >
                          {active ? "✓" : ""}
                        </span>
                        {perm.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={onClose}
            disabled={isBusy}
            className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Close
          </button>
          <button
            onClick={onAddMember}
            disabled={!selectedUserToAssign || rolePermLoading || isBusy}
            className="px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isBusy ? "Adding..." : "Add Member"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageMembersModal;
